import { useState } from 'react';
import {
  BottomSheetContainer,
  createUseBottomSheetModal,
  Flex,
  InputBox,
  PrimaryButton,
  Typography,
} from '@banhala/ui-kit';
import { bridge, isUnderAge14 } from 'shared/src/utils';
import { validateEmail } from '../../../EmailLogin/validation';
import { BirthdaySelector } from '../RegisterComplete/BirthdaySelector';
import { MobileForm } from './MobileForm';
import { TermsForm } from './TermsForm';

type RegisterFields = {
  email?: string;
  phone_number?: string;
  birth_date?: string;
};

type RegisterDrawerParam = {
  isShowEmail: boolean;
  isShowMobile: boolean;
  isShowBirthdate: boolean;
  isShowTerms: boolean;
  /** 확정 필드로 소셜 로그인 재제출. 실패 시 throw하면 드로어를 유지한다. */
  onSubmit: (fields: RegisterFields) => Promise<void>;
};

const RegisterDrawerContent = ({
  param,
  onSuccess,
}: {
  param: RegisterDrawerParam;
  onSuccess: () => void;
}) => {
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(''); // 인증 완료된 번호
  const [birthDate, setBirthDate] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const emailValid = !param.isShowEmail || validateEmail(email) == null;
  const mobileValid = !param.isShowMobile || phoneNumber !== '';
  const termsValid = !param.isShowTerms || termsAgreed;
  const isValid = emailValid && mobileValid && termsValid;

  const handleBirthdateChange = (selected: string) => {
    if (selected === '') return;
    if (isUnderAge14(selected)) {
      bridge.toast('만 14세 이상만 가입 가능해요');
      return;
    }
    setBirthDate(selected);
  };

  const handleSubmit = async () => {
    if (submitting || !isValid) return;
    setSubmitting(true);
    try {
      await param.onSubmit({
        email: param.isShowEmail ? email : undefined,
        phone_number: param.isShowMobile ? phoneNumber : undefined,
        birth_date:
          param.isShowBirthdate && birthDate !== '' ? birthDate : undefined,
      });
      onSuccess();
    } catch {
      // 에러 토스트는 onSubmit이 담당. 드로어를 유지하고 재시도 가능하게 둔다.
      setSubmitting(false);
    }
  };

  return (
    <Flex padding={{ horizontal: 16, top: 8, bottom: 24 }} gap={24}>
      {param.isShowEmail && (
        <Flex gap={8}>
          <Typography type={'subtitle2'} color={'content_primary'}>
            {'이메일'}
          </Typography>
          <InputBox
            type={'underline'}
            placeholder={'이메일 입력'}
            value={email}
            onChangeText={setEmail}
            keyboardType={'email-address'}
            autoCapitalize={'none'}
          />
        </Flex>
      )}
      {param.isShowMobile && <MobileForm onVerified={setPhoneNumber} />}
      {param.isShowBirthdate && (
        <Flex gap={8}>
          <Typography type={'subtitle2'} color={'content_primary'}>
            {'생년월일 (선택)'}
          </Typography>
          <BirthdaySelector value={birthDate} onChange={handleBirthdateChange} />
        </Flex>
      )}
      {param.isShowTerms && <TermsForm onChange={setTermsAgreed} />}

      <PrimaryButton
        variant={'fill'}
        color={'gray'}
        size={'xlarge'}
        state={submitting ? 'loading' : isValid ? undefined : 'disabled'}
        onPress={handleSubmit}
      >
        {'가입하기'}
      </PrimaryButton>
    </Flex>
  );
};

/**
 * 소셜 가입 보완 드로어 — 네이버/페북 (이메일/휴대폰SMS/생일/약관 조건부).
 * (webview RegisterDrawer 대응) 확정 필드로 param.onSubmit(재제출) 실행, 성공 시에만 닫는다.
 */
export const useRegisterDrawer = createUseBottomSheetModal<
  void,
  RegisterDrawerParam
>(({ confirm, cancel, param }) => (
  <BottomSheetContainer title={'가입하기'} onPressClose={cancel}>
    <RegisterDrawerContent param={param} onSuccess={confirm} />
  </BottomSheetContainer>
));
